// src/components/WarehousesInstructions.js
// Entrepôts — /warehouses-instructions. The warehouses the AI contacts:
// their addresses, its instructions, the B2B clients they ship for.
// Zones: header · « À vérifier » (no address: the AI cannot reach it; no
// instructions) · tiles (filter the list) · the list (search ?q, filter
// ?filter, page ?page) · creation modal (then the new warehouse opens).
// API: GET/POST /api/logistics/warehouses-edn, GET /api/logistics/clients-edn.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronRight, MailX, Plus, ScrollText, Search, TriangleAlert, Warehouse } from 'lucide-react';
import {
  Badge, Banner, Card, EmptyState, Kpi, Modal, PageHeader, Pagination, Segmented, Toast,
} from '../design/ui';
import WarehouseForm, { ClientPicker, EmailListEditor } from './WarehouseForm';
import {
  LIST_FILTERS, PAGE_SIZE, emptyWarehouseForm, getWarehouseEmails, hasInstructions, matchesFilter, matchesSearch,
  sortByName, validateWarehouseForm, warehousePayload, warehousesAttention, warehousesSummary,
} from './warehousesModel';
import { formatCount, formatShortDay, formatStamp } from './clientsShared';
import './WarehousesInstructions.css';

// Kept for any reader of the former exports.
export { getWarehouseEmails, validateWarehouseForm, warehousePayload, EmailListEditor, ClientPicker };

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const WarehousesInstructions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchQuery = searchParams.get('q') || '';
  const filter = LIST_FILTERS.includes(searchParams.get('filter')) ? searchParams.get('filter') : 'all';
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const requestedPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const updateListParams = useCallback((updates, { replace = false } = {}) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace });
  }, [setSearchParams]);

  const [warehouses, setWarehouses] = useState(null); // null = loading
  const [listError, setListError] = useState(null);
  const [clients, setClients] = useState(null); // null = unknown
  const [toast, setToast] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(emptyWarehouseForm);
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    const headers = authHeaders();
    axios.get('/api/logistics/warehouses-edn', { headers })
      .then((res) => setWarehouses(Array.isArray(res.data) ? res.data : []))
      .catch((error) => {
        console.error('Failed to fetch warehouses:', error);
        setWarehouses([]);
        setListError(t('warehousesInstructions.fetchError', 'Impossible de charger les entrepôts.'));
      });
    axios.get('/api/logistics/clients-edn', { headers })
      .then((res) => setClients(Array.isArray(res.data) ? res.data : []))
      .catch((error) => console.error('Failed to fetch clients:', error));
  }, [t]);

  const loading = warehouses === null;
  const list = useMemo(() => warehouses || [], [warehouses]);
  const summary = useMemo(() => warehousesSummary(list, clients), [list, clients]);
  const attention = useMemo(() => warehousesAttention(list), [list]);

  const searched = list.filter((w) => matchesSearch(w, searchQuery));
  const visible = sortByName(searched.filter((w) => matchesFilter(w, filter)));
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(requestedPage, totalPages);
  const pageRows = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const setFilter = (next) => updateListParams({ filter: next === 'all' ? null : next, page: null }, { replace: true });
  const openWarehouse = (id) => navigate(`/warehouses-instructions/${id}`);

  const handleCreate = async () => {
    const error = validateWarehouseForm(createForm, t);
    if (error) { setCreateError(error); return; }
    setCreating(true);
    setCreateError(null);
    try {
      const headers = authHeaders();
      const res = await axios.post('/api/logistics/warehouses-edn', warehousePayload(createForm), { headers });
      setShowCreateModal(false);
      setCreateForm(emptyWarehouseForm());
      setToast({ tone: 'success', text: t('warehousesInstructions.createSuccess', 'Entrepôt créé.') });
      const listRes = await axios.get('/api/logistics/warehouses-edn', { headers });
      setWarehouses(Array.isArray(listRes.data) ? listRes.data : []);
      if (res.data?.id) navigate(`/warehouses-instructions/${res.data.id}`);
    } catch (error) {
      console.error('Failed to create warehouse:', error);
      const detail = error.response?.data?.detail;
      setCreateError(typeof detail === 'string' ? detail : t('warehousesInstructions.createError', 'Échec de la création de l’entrepôt.'));
    } finally {
      setCreating(false);
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm(emptyWarehouseForm());
    setCreateError(null);
  };

  // ---- zones ----------------------------------------------------------------

  const subtitle = loading || summary.total === 0
    ? t('aiAutomationUi.warehouses.subtitleEmpty', 'Les entrepôts que l’IA contacte : leurs adresses, ses consignes et les clients B2B qu’ils servent.')
    : t('aiAutomationUi.warehouses.subtitle', '{{total}} entrepôts · {{reachable}} joignables par e-mail · {{clients}} clients B2B rattachés', {
      total: formatCount(summary.total), reachable: formatCount(summary.withEmail), clients: formatCount(summary.linkedClients),
    });

  const attentionText = (item) => (item.kind === 'noemail'
    ? {
      badge: t('aiAutomationUi.warehouses.attention.blocking', 'Bloquant'),
      title: t('aiAutomationUi.warehouses.attention.noemail', '{{count}} entrepôts sans adresse e-mail', { count: item.count }),
      detail: t('aiAutomationUi.warehouses.attention.noemailDetail', 'L’IA ne peut pas les contacter : {{names}}', { names: item.names.join(', ') }),
    }
    : {
      badge: t('aiAutomationUi.warehouses.attention.watch', 'À surveiller'),
      title: t('aiAutomationUi.warehouses.attention.noinstr', '{{count}} entrepôts sans consigne', { count: item.count }),
      detail: t('aiAutomationUi.warehouses.attention.noinstrDetail', 'L’IA les contacte sans consigne particulière : {{names}}', { names: item.names.join(', ') }),
    });

  const renderAttention = () => {
    if (!attention.length) return null;
    const blocking = attention.some((item) => item.tone === 'danger');
    return (
      <Card
        icon={TriangleAlert}
        iconTone={blocking ? 'danger' : 'warning'}
        title={t('aiAutomationUi.warehouses.attention.title', 'À vérifier')}
        badge={<Badge tone={blocking ? 'danger' : 'warning'}>{attention.length}</Badge>}
        flush
        className={blocking ? 'wh-v2__attention wh-v2__attention--danger' : 'wh-v2__attention'}
      >
        <ul className="u-list">
          {attention.map((item) => {
            const text = attentionText(item);
            return (
              <li className="u-row" key={item.key}>
                <Badge tone={item.tone} dot>{text.badge}</Badge>
                <div className="u-row__main">
                  <div className="u-row__title">{text.title}</div>
                  <div className="u-row__meta" title={text.detail}>{text.detail}</div>
                </div>
                <div className="u-row__side">
                  <button
                    type="button"
                    className="u-btn u-btn--secondary u-btn--sm"
                    onClick={() => (item.id ? openWarehouse(item.id) : setFilter(item.kind))}
                  >
                    {t('aiAutomationUi.warehouses.see', 'Voir')}
                    <ChevronRight aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    );
  };

  const renderKpis = () => {
    if (loading) {
      return (
        <div className="u-kpi-row wh-v2__kpis" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton wh-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (summary.total === 0) return null;
    const withClientsTile = summary.clientsTotal !== null && summary.clientsTotal > 0;
    return (
      <div className={`u-kpi-row wh-v2__kpis${withClientsTile ? '' : ' wh-v2__kpis--3'}`}>
        <Kpi
          icon={Warehouse}
          label={t('aiAutomationUi.warehouses.kpi.total', 'Entrepôts')}
          value={formatCount(summary.total)}
          context={t('aiAutomationUi.warehouses.kpi.totalContext', '{{count}} joignables par e-mail', { count: summary.withEmail })}
          meter={{ value: summary.withEmail, max: summary.total, label: t('aiAutomationUi.warehouses.kpi.totalContext', '{{count}} joignables par e-mail', { count: summary.withEmail }) }}
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <Kpi
          icon={ScrollText}
          tone={summary.noInstructions ? 'warning' : 'success'}
          label={t('aiAutomationUi.warehouses.kpi.instructions', 'Avec consignes')}
          value={formatCount(summary.withInstructions)}
          unit={`/ ${formatCount(summary.total)}`}
          context={summary.noInstructions
            ? t('aiAutomationUi.warehouses.kpi.instructionsMissing', '{{count}} à compléter', { count: summary.noInstructions })
            : t('aiAutomationUi.warehouses.kpi.instructionsAll', 'toutes renseignées')}
          contextTone={summary.noInstructions ? 'warning' : undefined}
          active={filter === 'noinstr'}
          onClick={() => setFilter('noinstr')}
        />
        <Kpi
          icon={MailX}
          tone={summary.noEmail ? 'danger' : 'success'}
          label={t('aiAutomationUi.warehouses.kpi.noEmail', 'Sans adresse')}
          value={formatCount(summary.noEmail)}
          context={summary.noEmail
            ? t('aiAutomationUi.warehouses.kpi.noEmailContext', 'l’IA ne peut pas les contacter')
            : t('aiAutomationUi.warehouses.kpi.noEmailNone', 'tous joignables')}
          contextTone={summary.noEmail ? 'danger' : undefined}
          active={filter === 'noemail'}
          onClick={() => setFilter('noemail')}
        />
        {withClientsTile && (
          <Kpi
            icon={Building2}
            label={t('aiAutomationUi.warehouses.kpi.clients', 'Clients B2B rattachés')}
            value={formatCount(summary.linkedClients)}
            unit={`/ ${formatCount(summary.clientsTotal)}`}
            context={t('aiAutomationUi.warehouses.kpi.clientsContext', '{{count}} clients sans entrepôt', { count: summary.unlinkedClients })}
            meter={{ value: summary.linkedClients, max: summary.clientsTotal, label: t('aiAutomationUi.warehouses.kpi.clients', 'Clients B2B rattachés') }}
          />
        )}
      </div>
    );
  };

  const filterLabels = {
    all: t('aiAutomationUi.warehouses.filters.all', 'Tous'),
    noemail: t('aiAutomationUi.warehouses.filters.noemail', 'Sans adresse'),
    noinstr: t('aiAutomationUi.warehouses.filters.noinstr', 'Sans consigne'),
  };
  const filterCounts = {
    all: searched.length,
    noemail: searched.filter((w) => matchesFilter(w, 'noemail')).length,
    noinstr: searched.filter((w) => matchesFilter(w, 'noinstr')).length,
  };

  const renderTable = () => {
    if (loading) {
      return (
        <div className="wh-v2__skeleton" aria-busy="true" aria-label={t('aiAutomationUi.warehouses.loading', 'Chargement des entrepôts')}>
          {Array.from({ length: 6 }, (_, i) => <span key={i} className="u-skeleton" />)}
        </div>
      );
    }
    if (!list.length) {
      if (listError) return null;
      return (
        <EmptyState
          icon={Warehouse}
          title={t('aiAutomationUi.warehouses.emptyTitle', 'Aucun entrepôt pour l’instant')}
          action={(
            <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => setShowCreateModal(true)}>
              <Plus aria-hidden="true" />
              {t('warehousesInstructions.createNew', 'Créer un entrepôt')}
            </button>
          )}
        >
          {t('warehousesInstructions.noWarehouses', 'Aucun entrepôt pour le moment. Créez-en un pour que l’IA sache qui contacter et comment.')}
        </EmptyState>
      );
    }
    if (!visible.length) {
      return (
        <EmptyState
          icon={Search}
          title={t('warehousesInstructions.noMatches', 'Aucun entrepôt ne correspond à la recherche.')}
          action={(
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => updateListParams({ q: null, filter: null, page: null }, { replace: true })}>
              {t('aiAutomationUi.warehouses.clearFilters', 'Effacer les filtres')}
            </button>
          )}
        >
          {t('aiAutomationUi.warehouses.noMatchHint', 'Changez la recherche ou le filtre.')}
        </EmptyState>
      );
    }
    return (
      <div className="u-table-scroll">
        <table className="u-table wh-v2__table">
          <thead>
            <tr>
              <th style={{ width: '26%' }}>{t('aiAutomationUi.warehouses.col.warehouse', 'Entrepôt')}</th>
              <th style={{ width: '28%' }}>{t('warehousesInstructions.contact', 'Contact')}</th>
              <th style={{ width: '16%' }}>{t('aiAutomationUi.warehouses.col.instructions', 'Consignes')}</th>
              <th style={{ width: '26%' }}>{t('aiAutomationUi.warehouses.col.clients', 'Clients B2B')}</th>
              <th style={{ width: '62px' }} aria-label={t('aiAutomationUi.warehouses.open', 'Ouvrir')} />
            </tr>
          </thead>
          <tbody>
            {pageRows.map((warehouse) => {
              const emails = getWarehouseEmails(warehouse);
              const clientList = warehouse.clients || [];
              const clientNames = clientList.map((c) => c.name).join(', ');
              return (
                <tr
                  key={warehouse.id}
                  className="wh-v2__row"
                  tabIndex={0}
                  onClick={() => openWarehouse(warehouse.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openWarehouse(warehouse.id);
                    }
                  }}
                >
                  <td title={warehouse.name}>
                    <span className="wh-v2__name">{warehouse.name}</span>
                    {warehouse.updated_at && (
                      <span className="u-cell-sub" title={formatStamp(warehouse.updated_at)}>
                        {t('aiAutomationUi.warehouses.updated', 'modifié le {{date}}', { date: formatShortDay(warehouse.updated_at) })}
                      </span>
                    )}
                  </td>
                  <td title={emails.join(', ')}>
                    {emails.length === 0 ? (
                      <Badge tone="danger" dot>{t('aiAutomationUi.warehouses.noAddress', 'Aucune adresse')}</Badge>
                    ) : (
                      <span className="wh-v2__first">
                        <span className="wh-v2__first-text">{emails[0]}</span>
                        {emails.length > 1 && <span className="wh-v2__more">+{emails.length - 1}</span>}
                      </span>
                    )}
                  </td>
                  <td>
                    {hasInstructions(warehouse)
                      ? <Badge tone="success">{t('aiAutomationUi.warehouses.instructionsSet', 'Renseignées')}</Badge>
                      : <Badge tone="warning" dot>{t('warehousesInstructions.instructionsMissing', 'À compléter')}</Badge>}
                  </td>
                  <td title={clientNames}>
                    {clientList.length === 0 ? (
                      <span className="u-muted">—</span>
                    ) : (
                      <span className="wh-v2__first">
                        <span className="wh-v2__count u-num">{clientList.length}</span>
                        <span className="wh-v2__first-text u-muted">{clientNames}</span>
                      </span>
                    )}
                  </td>
                  <td className="wh-v2__chevron"><ChevronRight aria-hidden="true" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderList = () => (
    <section className="u-card">
      {(loading || list.length > 0) && (
        <div className="u-toolbar">
          <div className="u-input-group u-input-group--lead">
            <Search className="u-input-group__icon" aria-hidden="true" />
            <input
              type="search"
              className="u-input"
              value={searchQuery}
              onChange={(e) => updateListParams({ q: e.target.value, page: null }, { replace: true })}
              placeholder={t('aiAutomationUi.warehouses.search', 'Nom, adresse ou client')}
              aria-label={t('common.search', 'Rechercher')}
            />
          </div>
          <Segmented
            label={t('aiAutomationUi.warehouses.filters.label', 'Filtrer les entrepôts')}
            value={filter}
            onChange={setFilter}
            options={LIST_FILTERS.map((key) => ({
              value: key,
              label: filterLabels[key],
              count: loading ? undefined : formatCount(filterCounts[key]),
            }))}
          />
          <span className="u-toolbar__spacer" />
          {!loading && (
            <span className="wh-v2__shown">
              {t('aiAutomationUi.warehouses.shown', '{{count}} affichés', { count: formatCount(visible.length) })}
            </span>
          )}
        </div>
      )}
      {renderTable()}
      {!loading && visible.length > PAGE_SIZE && (
        <Pagination
          page={currentPage}
          pageSize={PAGE_SIZE}
          total={visible.length}
          onChange={(next) => updateListParams({ page: next <= 1 ? null : next })}
          summary={({ first, last, total }) => t('aiAutomationUi.warehouses.pageSummary', '{{first}}–{{last}} sur {{total}}', { first, last, total: formatCount(total) })}
          previousLabel={t('common.previousPage', 'Page précédente')}
          nextLabel={t('common.nextPage', 'Page suivante')}
        />
      )}
    </section>
  );

  const renderCreate = () => (
    <Modal
      wide
      icon={Warehouse}
      title={t('warehousesInstructions.createNew', 'Créer un entrepôt')}
      description={t('aiAutomationUi.warehouses.createDesc', 'L’IA contacte l’entrepôt à ces adresses et suit vos consignes pour les prochains échanges.')}
      onClose={closeCreateModal}
      closeLabel={t('common.close', 'Fermer')}
      footer={(
        <>
          <button type="button" className="u-btn u-btn--secondary" onClick={closeCreateModal} disabled={creating}>
            {t('common.cancel', 'Annuler')}
          </button>
          <button type="button" className="u-btn u-btn--primary" onClick={handleCreate} disabled={creating}>
            {creating ? t('warehousesInstructions.creating', 'Création…') : t('warehousesInstructions.submitCreate', 'Créer l’entrepôt')}
          </button>
        </>
      )}
    >
      {createError && <div className="wh-v2__modal-error"><Banner tone="danger">{createError}</Banner></div>}
      <WarehouseForm
        form={createForm}
        setForm={(updater) => { setCreateForm(updater); setCreateError(null); }}
        clients={clients || []}
        t={t}
        idPrefix="wh-new"
      />
    </Modal>
  );

  return (
    <div className="u-page wh-v2">
      <PageHeader
        title={t('warehousesInstructions.title', 'Entrepôts')}
        subtitle={subtitle}
        actions={(
          <button type="button" className="u-btn u-btn--primary" onClick={() => setShowCreateModal(true)}>
            <Plus aria-hidden="true" />
            {t('warehousesInstructions.createNew', 'Créer un entrepôt')}
          </button>
        )}
      />

      <div className="u-well">
        {listError && <Banner tone="danger">{listError}</Banner>}
        {renderAttention()}
        {renderKpis()}
        {renderList()}
      </div>

      {showCreateModal && renderCreate()}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default WarehousesInstructions;
